<template lang="pug">
AppCard.ticket-card
  .ticket
    header.ticket__header
      .ticket__icon(:class="variant")
        i.uil(:class="icon" aria-hidden="true")
      .ticket__heading
        .ticket__id Ticket \#{{ ticket.id }}
        h3.ticket__title(:title="ticket.name") {{ ticket.name }}
      span.ticket__status(:class="variant") {{ stage }}
    .ticket__grid
      .ticket__item
        h4 {{ $t("common.service") }}
        span(:title="serviceName") {{ serviceName }}
      .ticket__item
        h4 {{ $t("ticket.create_date") }}
        span {{ formatDate(ticket.create_date) }}
      .ticket__item(v-if="isClosed")
        h4 {{ $t("ticket.close_date") }}
        span {{ formatDate(ticket.close_date) }}
</template>

<script>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import AppCard from "@/components/AppCard";
import {
  EMPTY_VALUE,
  isBlank,
  relationLabel,
  shortDate,
} from "@/utils/formatters";

export default {
  props: { ticket: Object },
  components: { AppCard },
  setup(props) {
    const { locale } = useI18n();

    const formatDate = (value) => shortDate(value, locale.value);

    const stage = computed(() => {
      const label = relationLabel(props.ticket.stage_id);
      return label === EMPTY_VALUE ? "" : label;
    });

    const isClosed = computed(() => !isBlank(props.ticket.close_date));

    // Stage names are configured on the backend, so the state is read from the
    // close date and only the cancelled wording is special-cased
    const isCancelled = computed(() =>
      /cancel/i.test(String(props.ticket.stage_id?.[1] || ""))
    );

    const variant = computed(() => {
      if (isCancelled.value) {
        return "neutral";
      }

      return isClosed.value ? "success" : "info";
    });

    const icon = computed(() => {
      if (isCancelled.value) {
        return "uil-times-circle";
      }

      return isClosed.value ? "uil-check-circle" : "uil-comment-alt-lines";
    });

    const serviceName = computed(() =>
      relationLabel(props.ticket.x_studio_servicio)
    );

    return {
      formatDate,
      icon,
      isClosed,
      serviceName,
      stage,
      variant,
    };
  },
};
</script>

<style lang="scss" scoped>
.ticket-card {
  @apply h-full;
}

.ticket {
  @apply flex;
  @apply flex-col;
  @apply h-full;

  &__header {
    @apply flex;
    @apply items-start;
    @apply gap-3;
  }

  &__icon {
    @apply flex;
    @apply flex-shrink-0;
    @apply items-center;
    @apply justify-center;
    @apply w-11;
    @apply h-11;
    @apply rounded-xl;

    i {
      @apply text-2xl;
      @apply leading-none;
    }

    &.success {
      @apply bg-gignetgreen-200;
      i {
        @apply text-gignetgreen-700;
      }
    }
    &.info {
      @apply bg-gignetblue-100;
      i {
        @apply text-gignetblue-500;
      }
    }
    &.neutral {
      @apply bg-bluegray-100;
      i {
        @apply text-bluegray-500;
      }
    }
  }

  &__heading {
    @apply flex-1;
    @apply min-w-0;
  }

  &__id {
    @apply text-xs;
    @apply text-bluegray-400;
    @apply uppercase;
    @apply tracking-wide;
  }

  &__title {
    @apply text-bluegray-700;
    @apply font-semibold;
    @apply leading-snug;
    // Two lines max: ticket subjects are free text
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__status {
    @apply flex-shrink-0;
    @apply px-2.5;
    @apply py-1;
    @apply rounded-full;
    @apply text-xs;
    @apply font-semibold;
    @apply leading-none;
    @apply whitespace-nowrap;

    &.success {
      @apply bg-gignetgreen-200;
      @apply text-gignetgreen-800;
    }
    &.info {
      @apply bg-gignetblue-100;
      @apply text-gignetblue-600;
    }
    &.neutral {
      @apply bg-bluegray-100;
      @apply text-bluegray-500;
    }
  }

  &__grid {
    @apply grid;
    @apply grid-cols-2;
    @apply gap-x-4;
    @apply gap-y-3;
    @apply mt-auto;
    @apply pt-4;
    @apply border-t;
    @apply border-bluegray-100;

    h4 {
      @apply text-xs;
      @apply font-medium;
      @apply text-bluegray-400;
      @apply uppercase;
      @apply tracking-wide;
      @apply truncate;
    }

    span {
      @apply block;
      @apply text-sm;
      @apply text-bluegray-700;
      @apply truncate;
    }
  }

  &__item {
    @apply min-w-0;
  }
}
</style>
